import React, { useState } from "react";
import { DesktopNavBarItem } from "@app.automotus.io/components/NavBar/NavBarItem/DesktopNavBarItem";
import { NavBarTarget } from "@app.automotus.io/components/NavBar";
import Box from "@mui/material/Box";
import { GetAllInvoicesData, GetPendingEnforcementIntentsData } from "common/graphql";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { useLogout } from "@app.automotus.io/components/auth";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import { Theme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import AccountContextDropdown from "@app.automotus.io/components/AccountContextDropdown";
import {
  GET_AVAILABLE_ACCOUNT_CONTEXTS,
  GetAvailableAccountContextsData,
} from "common/graphql/queries/GetAvailableAccountContexts";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    width: "160px",
    backgroundColor: theme.palette.background.paper,
    marginTop: `${theme.spacing(0.75)} !important`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
    borderRadius: theme.spacing(0.5),
    boxShadow: theme.shadows[3],
  },
}));

export const DesktopNavBarMenu: React.FC<DesktopNavBarMenuProps> = ({
  invoicesData,
  invoicesLoading,
  pendingEnforcementIntentsData,
  pendingEnforcementIntentsLoading,
  pendingPermitsCount,
  targets,
  supportLabel = "Support",
  supportUrl,
  resourcesLabel = "Resources",
  resourcesUrl,
}) => {
  const { userProfile } = useUserProfile();
  const logout = useLogout();
  const navigate = useNavigate();
  const { data: contextsData, loading: contextsLoading } =
    useQuery<GetAvailableAccountContextsData>(GET_AVAILABLE_ACCOUNT_CONTEXTS);
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);

  const availableContexts = contextsData?.availableAccountContexts ?? [];
  const showContextSwitcher = !contextsLoading && availableContexts.length > 1;

  return (
    <>
      <Box display={"flex"} flexDirection={"row"} justifyContent={"flex-end"} alignItems={"center"}>
        <Stack direction={"row"} spacing={2.5} alignItems="center">
          {targets
            .filter((target) => target.title !== "Logout")
            .map((target) => (
              <DesktopNavBarItem
                key={target.to}
                invoicesData={invoicesData}
                invoicesLoading={invoicesLoading}
                pendingEnforcementIntentsData={pendingEnforcementIntentsData}
                pendingEnforcementIntentsLoading={pendingEnforcementIntentsLoading}
                pendingPermitsCount={pendingPermitsCount}
                {...target}
              />
            ))}
          {userProfile && (
            <CustomTooltip
              placement="bottom-end"
              title={
                <Stack spacing={1}>
                  {showContextSwitcher && (
                    <>
                      <Button
                        variant="text"
                        sx={{ textTransform: "none" }}
                        fullWidth
                        onClick={() => setIsAccountDialogOpen(true)}
                      >
                        Change Account
                      </Button>
                      <Divider />
                    </>
                  )}
                  {supportUrl && (
                    <Button
                      component={"a" as const}
                      target="_blank"
                      href={supportUrl}
                      sx={{
                        textTransform: "none",
                      }}
                    >
                      {supportLabel}
                    </Button>
                  )}
                  {resourcesUrl && (
                    <Button
                      component={"a" as const}
                      target="_blank"
                      href={resourcesUrl}
                      sx={{
                        textTransform: "none",
                      }}
                    >
                      {resourcesLabel}
                    </Button>
                  )}
                  <Button
                    variant="text"
                    sx={{
                      color: (theme: Theme) => theme.palette.error.main,
                      textTransform: "none",
                      py: (theme: Theme) => theme.spacing(0.75),
                      px: (theme: Theme) => theme.spacing(1),
                      my: "auto",
                      fontSize: "1rem",
                      fontWeight: 400,
                    }}
                    fullWidth
                    onClick={async (e: React.SyntheticEvent) => {
                      e.preventDefault();
                      await logout();
                    }}
                  >
                    Logout
                  </Button>
                </Stack>
              }
            >
              <Box
                data-testid="account-menu-trigger"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: (theme: Theme) => theme.spacing(0.5),
                  border: "1px solid",
                  borderColor: "primary.main",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                  color: "primary.main",
                  fontSize: "20px",
                  fontWeight: 400,
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigate("/account");
                }}
              >
                {userProfile?.givenName?.charAt(0).toUpperCase()}
                {userProfile?.familyName?.charAt(0).toUpperCase()}
              </Box>
            </CustomTooltip>
          )}
        </Stack>
      </Box>
      <Dialog open={isAccountDialogOpen} onClose={() => setIsAccountDialogOpen(false)}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
          Change Account
          <IconButton size="small" onClick={() => setIsAccountDialogOpen(false)} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3, px: 3, minWidth: 280 }}>
          <AccountContextDropdown />
        </DialogContent>
      </Dialog>
    </>
  );
};

export interface DesktopNavBarMenuProps {
  invoicesData?: GetAllInvoicesData;
  invoicesLoading?: boolean;
  pendingEnforcementIntentsData?: GetPendingEnforcementIntentsData;
  pendingEnforcementIntentsLoading?: boolean;
  pendingPermitsCount?: number;
  targets: NavBarTarget[];
  supportLabel?: string;
  supportUrl?: string;
  /** Label for the Resources entry in the account dropdown menu. Defaults to "Resources". */
  resourcesLabel?: string;
  /** URL for the Resources entry in the account dropdown menu. Omit to not render it. */
  resourcesUrl?: string;
}
